import { LONG_SUBMISSION_PAGES, TRIAL_CREDITS } from '@aimfora/billing'
import type { CompanyDetails } from '@aimfora/config'
import { m } from '@aimfora/i18n'

export type LegalBlock = { type: 'p'; text: string } | { type: 'ul'; items: string[] }

export interface LegalSection {
  id: string
  title: string
  blocks: LegalBlock[]
}

/** The date of the last change to either text. Bump it whenever the catalog text changes. */
export const LEGAL_UPDATED = '2026-11-01'

const p = (text: string): LegalBlock => ({ type: 'p', text })
const ul = (...items: string[]): LegalBlock => ({ type: 'ul', items })

/** A mail link in the inline markup of the legal texts, or a placeholder until the address is set. */
export function contactLink(company: CompanyDetails): string {
  return company.email ? `[${company.email}](mailto:${company.email})` : m.legal_placeholder_email()
}

/** Who runs the service. Missing details show as placeholders, and the page says it is a draft. */
export function companyLine(company: CompanyDetails): string {
  return m.legal_company({
    name: company.name ?? m.legal_placeholder_name(),
    cvr: company.cvr ?? m.legal_placeholder_cvr(),
    address: company.address ?? m.legal_placeholder_address(),
    contact: contactLink(company),
  })
}

export function privacySections(company: CompanyDetails): LegalSection[] {
  return [
    {
      id: 'who',
      title: m.privacy_who_title(),
      blocks: [p(m.privacy_who_1()), p(companyLine(company))],
    },
    {
      id: 'roles',
      title: m.privacy_roles_title(),
      blocks: [p(m.privacy_roles_1()), p(m.privacy_roles_2())],
    },
    {
      id: 'data',
      title: m.privacy_data_title(),
      blocks: [
        ul(
          m.privacy_data_account(),
          m.privacy_data_org(),
          m.privacy_data_profile(),
          m.privacy_data_classes(),
          m.privacy_data_work(),
          m.privacy_data_usage(),
          m.privacy_data_analytics(),
          m.privacy_data_feedback(),
          m.privacy_data_mail(),
          m.privacy_data_beta(),
          m.privacy_data_billing(),
        ),
      ],
    },
    {
      id: 'purpose',
      title: m.privacy_purpose_title(),
      blocks: [
        ul(
          m.privacy_purpose_1(),
          m.privacy_purpose_2(),
          m.privacy_purpose_3(),
          m.privacy_purpose_4(),
          m.privacy_purpose_5(),
        ),
      ],
    },
    {
      id: 'ai',
      title: m.privacy_ai_title(),
      blocks: [p(m.privacy_ai_1()), p(m.privacy_ai_2()), p(m.privacy_ai_3())],
    },
    {
      id: 'recipients',
      title: m.privacy_recipients_title(),
      blocks: [
        p(m.privacy_recipients_1()),
        ul(
          m.privacy_recipient_hosting(),
          m.privacy_recipient_storage(),
          m.privacy_recipient_email(),
          m.privacy_recipient_ai(),
          m.privacy_recipient_stripe(),
          m.privacy_recipient_posthog(),
        ),
        p(m.privacy_recipients_2()),
      ],
    },
    {
      id: 'admin',
      title: m.privacy_admin_title(),
      blocks: [p(m.privacy_admin_1()), p(m.privacy_admin_2()), p(m.privacy_admin_3())],
    },
    {
      id: 'retention',
      title: m.privacy_retention_title(),
      blocks: [
        ul(
          m.privacy_retention_1(),
          m.privacy_retention_2(),
          m.privacy_retention_3(),
          m.privacy_retention_4(),
          m.privacy_retention_5(),
          m.privacy_retention_6(),
          m.privacy_retention_7(),
          m.privacy_retention_8(),
          m.privacy_retention_9(),
        ),
      ],
    },
    {
      id: 'cookies',
      title: m.privacy_cookies_title(),
      blocks: [p(m.privacy_cookies_1())],
    },
    {
      id: 'rights',
      title: m.privacy_rights_title(),
      blocks: [p(m.privacy_rights_1({ contact: contactLink(company) })), p(m.privacy_rights_2())],
    },
    {
      id: 'changes',
      title: m.privacy_changes_title(),
      blocks: [p(m.privacy_changes_1())],
    },
  ]
}

export function termsSections(company: CompanyDetails): LegalSection[] {
  return [
    {
      id: 'parties',
      title: m.terms_parties_title(),
      blocks: [p(companyLine(company)), p(m.terms_parties_1())],
    },
    { id: 'service', title: m.terms_service_title(), blocks: [p(m.terms_service_1())] },
    { id: 'account', title: m.terms_account_title(), blocks: [p(m.terms_account_1())] },
    { id: 'beta', title: m.terms_beta_title(), blocks: [p(m.terms_beta_1())] },
    {
      id: 'plans',
      title: m.terms_plans_title(),
      blocks: [
        p(m.terms_plans_1({ trial: String(TRIAL_CREDITS) })),
        p(m.terms_plans_2({ pages: String(LONG_SUBMISSION_PAGES) })),
        p(m.terms_plans_3()),
      ],
    },
    { id: 'payment', title: m.terms_payment_title(), blocks: [p(m.terms_payment_1())] },
    { id: 'cancel', title: m.terms_cancel_title(), blocks: [p(m.terms_cancel_1())] },
    { id: 'withdrawal', title: m.terms_withdrawal_title(), blocks: [p(m.terms_withdrawal_1())] },
    { id: 'liability', title: m.terms_liability_title(), blocks: [p(m.terms_liability_1())] },
    { id: 'data', title: m.terms_data_title(), blocks: [p(m.terms_data_1())] },
    { id: 'changes', title: m.terms_changes_title(), blocks: [p(m.terms_changes_1())] },
    {
      id: 'complaints',
      title: m.terms_complaints_title(),
      blocks: [p(m.terms_complaints_1({ contact: contactLink(company) }))],
    },
  ]
}
